import React, { useEffect } from "react";
import {
  Button,
  Col,
  Input,
  Row,
  Select,
  message,
  Spin,
  Descriptions,
  InputNumber,
  Modal,
} from "antd";
import FormLayout, { FormSection, FormItem } from "Components/form";
import { Controller, useForm } from "react-hook-form";
import DatePicker from "Components/date-picker-localized";
import { RULES } from "Constants/rules";
import { fixDates } from "Utils/";
import { createComponentStore } from "easy-peasy";
import { yupResolver } from "@hookform/resolvers";
import {
  HousingScoringStoreModel,
  ACTION_TYPES,
} from "store/models/master-data/housing-scoring";
import { HousingScoringFormValidationModel } from "ValidationModels/housing-scoring";
import TitleOrderInput from "./components/title-order";
import TaskOrderInput from "./components/task-order";
import TermOfOfficeInput from "./components/term-of-office";
import MarriedStatusInput from "./components/married-status";
import ChildrenInput from "./components/children";
import HousingPreviouslyScoreInput from "./components/housing-previously";
import ManicipalityContiguousInInput from "./components/municipality-contiguous-in";
import ManicipalityContiguousOutInput from "./components/municipality-contiguous-out";
import Total from "./components/total";
import { DISPLAY_MODE, HOUSING_SCORING_POINT_TYPE } from "Constants";
import PartnerSelect from "Components/partner-select";

const useStore = createComponentStore(HousingScoringStoreModel);

export default function HousingScoringForm({
  housingScoring,
  onAfterSuccess,
  loading = false,
  mode = DISPLAY_MODE.READ_ONLY,
  ...props
}) {
  const [store, actions] = useStore();

  const IsLoading = (process) => {
    return [ACTION_TYPES.FETCHING_DATA].includes(process);
  };

  const form = useForm({
    mode: "onChange",
    resolver: yupResolver(HousingScoringFormValidationModel),
    defaultValues: housingScoring,
  });

  const {
    control,
    handleSubmit,
    errors,
    formState,
    reset,
    watch,
    setValue,
  } = form;

  const values = watch();

  const total =
    values.childrenScore +
    values.disabilityFamilyMembersScore +
    values.disabilitySituationScore +
    values.housingDemandWaitingScore +
    values.housingMunicipalityContiguousInScore +
    values.housingMunicipalityContiguousOutScore +
    values.housingPreviouslyScore +
    values.obligatedOtherPerson +
    values.salaryExceptScore +
    values.termOfOfficeScore +
    values.veteranScore +
    values.wifeScore;

  useEffect(() => {
    if (housingScoring) {
      let values = fixDates({ ...housingScoring });
      values.married = values.wifeScore > 0 ? true : false;
      values.termOfOffice = values.termOfOfficeScore / 5;
      values.children = values.childrenScore / 3;
      values.housingPreviously = (values.housingPreviouslyScore / 3) * -1;
      values.housingMunicipalityContiguousIn =
        (values.housingMunicipalityContiguousInScore / 15) * -1;
      values.housingMunicipalityContiguousOut =
        (values.housingMunicipalityContiguousOutScore / 10) * -1;

      reset(values);
    }
  }, [housingScoring]);

  useEffect(() => {
    if (store.response)
      switch (store.process) {
        case ACTION_TYPES.CREATED_DATA:
          message.success("Konut puanlama formu oluşturuldu.");
          onAfterSuccess(store.response);
          break;
        case ACTION_TYPES.UPDATED_DATA:
          message.success("Konut puanlama formu güncellendi.");
          onAfterSuccess(store.response);
          break;
        default:
          break;
      }
  }, [store.process, store.response]);

  const formOnSubmit = async (form) => {
    if (mode === DISPLAY_MODE.EDIT)
      actions.update({ ...housingScoring, ...form });
    if (mode === DISPLAY_MODE.NEW) actions.create(form);
  };

  return (
    <Spin spinning={IsLoading(store.process)}>
      <form onSubmit={handleSubmit(formOnSubmit)} style={{ padding: "0 24px" }}>
        <FormLayout>
          <Col xs={24}>
            <FormSection title="Genel">
              <Row gutter={16}>
                <Col xs={24} md={8}>
                  <FormItem
                    htmlFor="partner"
                    error={errors.partner?.id}
                    label="Muhatap"
                    required={true}
                  >
                    <PartnerSelect
                      form={form}
                      objectInValue={true}
                      field="partner"
                      defaultValue={housingScoring.partner}
                      params={{
                        ruleCode: RULES.RULE0018,
                      }}
                      readOnly={mode === DISPLAY_MODE.READ_ONLY}
                      style={{ width: "100%" }}
                    />
                  </FormItem>
                </Col>
                <Col xs={24} md={8}>
                  <FormItem
                    label="Başvuru Tarihi"
                    htmlFor="dateOfApplication"
                    error={errors.dateOfApplication}
                    required={true}
                  >
                    <Controller
                      control={control}
                      name="dateOfApplication"
                      as={
                        <DatePicker
                          readOnly={mode === DISPLAY_MODE.READ_ONLY}
                          format="DD.MM.YYYY"
                          style={{ width: "100%" }}
                        />
                      }
                    />
                  </FormItem>
                </Col>

                <Col xs={24} md={8}>
                  <FormItem
                    htmlFor="pointType"
                    required={true}
                    error={errors.pointType}
                    label="Puanlama Türü"
                  >
                    <Controller
                      control={control}
                      name="pointType"
                      as={
                        <Select
                          className={
                            mode === DISPLAY_MODE.READ_ONLY ? "readOnly" : null
                          }
                        >
                          {Object.entries(HOUSING_SCORING_POINT_TYPE).map(
                            ([key, value]) => (
                              <Select.Option key={key} value={key}>
                                {value}
                              </Select.Option>
                            )
                          )}
                        </Select>
                      }
                    />
                  </FormItem>
                </Col>

                <Col xs={24} md={8}>
                  <FormItem label="Görev Ayrım Unvanı">
                    <Input
                      readOnly
                      value={values?.partner?.taskDistinctionTitle?.description}
                    />
                  </FormItem>
                </Col>

                <Col xs={24} md={8}>
                  <FormItem label="Görev">
                    <Input
                      readOnly
                      value={values?.partner?.partnerTask?.description}
                    />
                  </FormItem>
                </Col>
              </Row>
            </FormSection>
            <FormSection title="Puanlar">
              <Row gutter={16}>
                <Descriptions size="small" column={1} bordered>
                  <Descriptions.Item label="Unvan Sırası">
                    <TitleOrderInput form={form} partner={values?.partner} />
                  </Descriptions.Item>
                  <Descriptions.Item label="Görev Sırası">
                    <TaskOrderInput form={form} partner={values?.partner} />
                  </Descriptions.Item>
                  <Descriptions.Item label="Personelin, aylık ve özlük hakları ile ilgili gelirleri hariç olmak üzere, kendisinin ve kanunen bakmakla mükellef bulunduğu ve konutta birlikte oturacağı aile fertlerinin, konut kira gelirleri dışındaki diğer tüm sürekli gelirlerinin yıllık toplamının, 15.000 gösterge rakamının memur maaş katsayısı ile çarpımı sonucu bulunacak miktarı geçmesi halinde (-1) puan">
                    <Controller
                      defaultValue={0}
                      control={form.control}
                      name="salaryExceptScore"
                      as={
                        <InputNumber
                          readOnly={mode === DISPLAY_MODE.READ_ONLY}
                          inputmode="numeric"
                          style={{ width: "100%" }}
                          min={-1}
                          max={0}
                        />
                      }
                    />
                  </Descriptions.Item>
                  <Descriptions.Item label="Hizmet Süresi Her Yıl İçin (+5)">
                    <TermOfOfficeInput
                      form={form}
                      termOfOffice={values?.termOfOffice}
                      mode={mode}
                    />
                  </Descriptions.Item>
                  <Descriptions.Item label="Eşi İçin (+6)">
                    <MarriedStatusInput
                      form={form}
                      married={values?.married}
                      mode={mode}
                    />
                  </Descriptions.Item>
                  <Descriptions.Item label="Çocukları Her biri İçin (Yalnızca İki Çocuğa Kadar) (+3)">
                    <ChildrenInput
                      form={form}
                      children={values?.children}
                      mode={mode}
                    />
                  </Descriptions.Item>
                  <Descriptions.Item label="Bakmakla Yükümlü Olduğu Diğer Her Fert İçin (+1)">
                    <Controller
                      control={form.control}
                      defaultValue={0}
                      name="obligatedOtherPerson"
                      as={
                        <InputNumber
                          readOnly={mode === DISPLAY_MODE.READ_ONLY}
                          style={{ width: "100%" }}
                          min={0}
                          inputmode="numeric"
                        />
                      }
                    />
                  </Descriptions.Item>
                  <Descriptions.Item label="Konut Tahsisi İçin Beklediği Her Yıl İçin (+1)">
                    <Controller
                      defaultValue={0}
                      control={form.control}
                      name="housingDemandWaitingScore"
                      as={
                        <InputNumber
                          readOnly={mode === DISPLAY_MODE.READ_ONLY}
                          style={{ width: "100%" }}
                          min={0}
                          inputmode="numeric"
                        />
                      }
                    />
                  </Descriptions.Item>
                  <Descriptions.Item label="Daha Önce Konuttan Yararlandığı Her Yıl İçin ( -3)">
                    <HousingPreviouslyScoreInput
                      form={form}
                      housingPreviously={values?.housingPreviously}
                      mode={mode}
                    />
                  </Descriptions.Item>
                  <Descriptions.Item label="Bakmakla Yükümlü olduğu Aile Fertlerinin Belediye Mücavir alan İçinde Konutu Olanlar ( -15)">
                    <ManicipalityContiguousInInput
                      form={form}
                      housingMunicipalityContiguousIn={
                        values?.housingMunicipalityContiguousIn
                      }
                      mode={mode}
                    />
                  </Descriptions.Item>
                  <Descriptions.Item label="Bakmakla Yükümlü olduğu Aile Fertlerinin Belediye Mücavir alan Dışında Konutu Olanlar ( -10)">
                    <ManicipalityContiguousOutInput
                      form={form}
                      housingMunicipalityContiguousOut={
                        values?.housingMunicipalityContiguousOut
                      }
                      mode={mode}
                    />
                  </Descriptions.Item>
                  <Descriptions.Item label="Engel Durumu">
                    <Controller
                      defaultValue={0}
                      control={form.control}
                      name="disabilitySituationScore"
                      as={
                        <InputNumber
                          readOnly={mode === DISPLAY_MODE.READ_ONLY}
                          style={{ width: "100%" }}
                          min={0}
                          inputmode="numeric"
                        />
                      }
                    />
                  </Descriptions.Item>
                  <Descriptions.Item label="Kanunen Bakmakla Mükellef Olunan ve Konutta Beraber Oturacak Olan Engelli Aile Fertleri">
                    <Controller
                      defaultValue={0}
                      control={form.control}
                      name="disabilityFamilyMembersScore"
                      as={
                        <InputNumber
                          readOnly={mode === DISPLAY_MODE.READ_ONLY}
                          style={{ width: "100%" }}
                          min={0}
                          inputmode="numeric"
                        />
                      }
                    />
                  </Descriptions.Item>
                  <Descriptions.Item label="Gazi veya Şehit Yakını">
                    <Controller
                      defaultValue={0}
                      control={form.control}
                      name="veteranScore"
                      as={
                        <InputNumber
                          readOnly={mode === DISPLAY_MODE.READ_ONLY}
                          style={{ width: "100%" }}
                          min={0}
                          inputmode="numeric"
                        />
                      }
                    />
                  </Descriptions.Item>
                  <Descriptions.Item label="TAHSİSE ESAS PUAN">
                    <Total form={form} total={total} />
                  </Descriptions.Item>
                </Descriptions>
              </Row>
            </FormSection>
            {mode === DISPLAY_MODE.READ_ONLY ? null : (
              <Row
                justify="space-between"
                style={{
                  borderTop: "1px dashed rgb(166, 157, 152)",
                  position: "sticky",
                  bottom: 0,
                  padding: "16px 0",
                  backgroundColor: "#f2f2f2",
                }}
              >
                <Col>
                  <Button
                    loading={
                      store.process === ACTION_TYPES.CREATING_DATA ||
                      store.process === ACTION_TYPES.UPDATING_DATA
                    }
                    style={{ width: 120, marginRight: 8 }}
                    type="primary"
                    htmlType="submit"
                  >
                    Kaydet
                  </Button>

                  <Button
                    onClick={() => {
                      Modal.confirm({
                        title: "İptal",
                        content:
                          "Kaydetmediğiniz tüm değişiklikler, geri alınacaktır.",
                        okText: "Evet",
                        cancelText: "İptal",
                        onOk: () => {
                          reset();
                        },
                      });
                    }}
                  >
                    İptal
                  </Button>
                </Col>
              </Row>
            )}
          </Col>
        </FormLayout>
      </form>
    </Spin>
  );
}
